import type {
  PendingSettlementsByVesselData,
  PendingSettlementsVessel,
} from '../../utils/client-reports/types';
import {
  EmptySection,
  ReportTable,
  SectionHeader,
  StatusText,
  type ReportColumn,
} from './ReportBlocks';
import { titleCase } from './reportLayout';
import { useReportFormat } from './useReportFormat';

// Lo sin liquidar por nave o vuelo: una fila por nave y ETA, con cuántos
// embarques lleva y el estado más atrasado. El detalle por contenedor va
// después.
const PendingSettlementsByVesselView = ({
  number: sectionNumber,
  data,
}: {
  number: number;
  data: PendingSettlementsByVesselData;
}) => {
  const { t, number, date, plural } = useReportFormat();
  const title = t('Liquidaciones por nave');

  if (data.rows.length === 0) {
    return (
      <section className='flex flex-col gap-6'>
        <SectionHeader number={sectionNumber} title={title} />
        <EmptySection>{t('No hay liquidaciones vencidas ni por vencer.')}</EmptySection>
      </section>
    );
  }

  const vesselName = (row: PendingSettlementsVessel) =>
    row.vessel ||
    (row.transport === 'A' ? t('Aéreo') : row.transport === 'T' ? t('Terrestre') : '—');
  // "2 críticas · 1 vencida": solo los estados que tiene la nave.
  const breakdown = (row: PendingSettlementsVessel) =>
    [
      row.byStatus.critical &&
        plural(row.byStatus.critical, '{{n}} crítica', '{{n}} críticas'),
      row.byStatus.overdue &&
        plural(row.byStatus.overdue, '{{n}} vencida', '{{n}} vencidas'),
      row.byStatus.dueSoon &&
        plural(row.byStatus.dueSoon, '{{n}} por vencer', '{{n}} por vencer'),
    ]
      .filter(Boolean)
      .join(' · ');

  // Lo que el cliente necesita para actuar va primero (nave, estado y
  // atraso): en celular la tabla se desplaza.
  const hasDestination = data.rows.some((row) => row.destinations.length > 0);
  const allColumns: ReportColumn<PendingSettlementsVessel>[] = [
    {
      key: 'vessel',
      label: t('Nave / vuelo'),
      render: (row) => <span className='font-medium'>{vesselName(row)}</span>,
    },
    {
      key: 'status',
      label: t('Estado'),
      render: (row) => (
        <span className='flex flex-col gap-0.5'>
          {row.status === 'critical' ? (
            <StatusText tone='danger'>{t('Crítica')}</StatusText>
          ) : row.status === 'overdue' ? (
            <StatusText tone='warning'>{t('Vencida')}</StatusText>
          ) : (
            <StatusText tone='muted'>{t('Por vencer')}</StatusText>
          )}
          {row.shipments > 1 ? (
            <span className='text-xs text-default-500'>{breakdown(row)}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'days',
      label: t('Días desde ETA'),
      align: 'right',
      render: (row) => number(row.daysSinceEta),
    },
    {
      key: 'shipments',
      label: t('Embarques'),
      align: 'right',
      render: (row) => number(row.shipments),
    },
    {
      key: 'ceq',
      label: t('Cajas eq.'),
      align: 'right',
      render: (row) => number(row.ceq),
    },
    {
      key: 'eta',
      label: t('ETA'),
      nowrap: true,
      render: (row) => date(row.eta),
    },
    {
      key: 'destination',
      label: t('Puerto de destino'),
      render: (row) =>
        row.destinations.length ? row.destinations.map(titleCase).join(', ') : '—',
    },
  ];
  // Si ningún embarque trae puerto (hay exportadoras que no lo cargan), la
  // columna no se muestra.
  const columns = hasDestination
    ? allColumns
    : allColumns.filter((column) => column.key !== 'destination');

  return (
    <section className='flex flex-col gap-8'>
      <SectionHeader
        number={sectionNumber}
        title={title}
        aside={`${plural(data.rows.length, '{{n}} nave', '{{n}} naves')} · ${plural(
          data.totals.shipments,
          '{{n}} embarque',
          '{{n}} embarques'
        )} · ${t('{{ceq}} cajas eq.', { ceq: number(data.totals.ceq) })}`}
      />
      <ReportTable<PendingSettlementsVessel>
        label={title}
        rows={data.rows}
        rowKey={(row) => [row.transport, row.vessel, row.eta].join('|')}
        columns={columns}
        total={{
          vessel: `${t('Total')} · ${plural(data.rows.length, '{{n}} nave', '{{n}} naves')}`,
          shipments: number(data.totals.shipments),
          ceq: number(data.totals.ceq),
        }}
      />
    </section>
  );
};

export default PendingSettlementsByVesselView;
